import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Plus, Trash2, ChevronLeft, ChevronRight, Save, Link2, X, FileText, Paperclip } from "lucide-react";
import { EMPTY_APPROVAL, fmtEUR } from "./approvalsConstants";

const API = process.env.REACT_APP_BACKEND_URL;
const STEPS = ["Datos del proyecto", "Convocatoria y socios", "Económico", "Resultados y documentos"];

function NumInput({ value, onChange, ...rest }) {
  return (
    <Input
      type="number"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      {...rest}
    />
  );
}

export function ApprovalFormDialog({ open, onOpenChange, initialData, onSave, saving, token }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialData || EMPTY_APPROVAL);
  const [initiatives, setInitiatives] = useState([]);
  const [showInitSelector, setShowInitSelector] = useState(false);
  const [initSearch, setInitSearch] = useState("");
  const [documents, setDocuments] = useState([]);
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [docSearch, setDocSearch] = useState("");

  // Reset form when dialog opens/closes or initialData changes
  useEffect(() => {
    if (open) setForm(initialData || EMPTY_APPROVAL);
  }, [open, initialData]);

  // Load initiatives when selector is opened
  useEffect(() => {
    if (!showInitSelector || initiatives.length > 0 || !token) return;
    fetch(`${API}/api/initiatives`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setInitiatives(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [showInitSelector, initiatives.length, token]);

  // Load repository documents lazily once
  useEffect(() => {
    if (!open || docsLoaded || !token) return;
    fetch(`${API}/api/documents`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        setDocuments(Array.isArray(d) ? d : []);
        setDocsLoaded(true);
      })
      .catch(() => setDocsLoaded(true));
  }, [open, docsLoaded, token]);

  const toggleDoc = (docId) => {
    setForm((f) => {
      const ids = f.attached_document_ids || [];
      return {
        ...f,
        attached_document_ids: ids.includes(docId)
          ? ids.filter((x) => x !== docId)
          : [...ids, docId],
      };
    });
  };

  const linkInitiative = (init) => {
    setForm((f) => ({
      ...f,
      rd_project_id: init.id,
      project_name: f.project_name || init.name || "",
      objective: f.objective || init.description || init.strategic_objective || "",
      socios: f.socios || init.partner || "",
      convocatoria: f.convocatoria || init.convocatoria || "",
      presupuesto_total: f.presupuesto_total || init.budget_total || init.budget_estimated || 0,
      financial: {
        ...f.financial,
        presupuesto: f.financial.presupuesto || init.budget_total || init.budget_estimated || 0,
        subvencion: f.financial.subvencion || init.external_funds || 0,
      },
      start_date: f.start_date || (init.start_date ? init.start_date.slice(0, 10) : ""),
      end_date: f.end_date || (init.end_date ? init.end_date.slice(0, 10) : ""),
    }));
    setShowInitSelector(false);
  };

  const unlinkInitiative = () => setForm((f) => ({ ...f, rd_project_id: null }));

  const linkedInitiative = form.rd_project_id ? initiatives.find((i) => i.id === form.rd_project_id) : null;

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const updateNested = (group, field, value) =>
    setForm((f) => ({ ...f, [group]: { ...f[group], [field]: value } }));
  const updateOutcome = (idx, value) =>
    setForm((f) => {
      const next = [...(f.success_outcomes || [])];
      next[idx] = value;
      return { ...f, success_outcomes: next };
    });
  const addOutcome = () =>
    setForm((f) => ({ ...f, success_outcomes: [...(f.success_outcomes || []), ""] }));
  const removeOutcome = (idx) =>
    setForm((f) => ({
      ...f,
      success_outcomes: f.success_outcomes.filter((_, i) => i !== idx),
    }));

  const totalCosts = Object.values(form.costs || {}).reduce((a, b) => a + (Number(b) || 0), 0);

  const canSave = !!form.project_name?.trim() && !!form.objective?.trim();
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="approval-form-dialog">
        <DialogHeader>
          <DialogTitle>
            Nueva ficha de aprobación
            <span className="ml-3 text-xs font-normal text-muted-foreground">
              Paso {step + 1} de {STEPS.length} · {STEPS[step]}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex gap-1 mb-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded ${i <= step ? "bg-primary" : "bg-muted"}`}
            />
          ))}
        </div>

        {/* STEP 0: Project basics */}
        {step === 0 && (
          <div className="space-y-3">
            {/* Portfolio link */}
            {form.rd_project_id ? (
              <div className="flex items-center justify-between p-2.5 rounded-lg border-2 border-primary/30 bg-primary/5">
                <div className="flex items-center gap-2 min-w-0">
                  <Link2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-primary font-bold">Vinculado al Portfolio</div>
                    <div className="text-sm font-medium truncate">
                      {linkedInitiative?.name || "Iniciativa vinculada"}
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={unlinkInitiative} data-testid="form-unlink-initiative">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : showInitSelector ? (
              <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Selecciona un proyecto del Portfolio</Label>
                  <Button variant="ghost" size="sm" onClick={() => setShowInitSelector(false)}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Input
                  value={initSearch}
                  onChange={(e) => setInitSearch(e.target.value)}
                  placeholder="Buscar iniciativa..."
                  className="h-8"
                  data-testid="form-init-search"
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {initiatives
                    .filter((i) =>
                      !initSearch || (i.name || "").toLowerCase().includes(initSearch.toLowerCase())
                    )
                    .slice(0, 30)
                    .map((i) => (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => linkInitiative(i)}
                        className="w-full text-left px-3 py-2 text-sm rounded hover:bg-primary/10 hover:text-primary border border-transparent hover:border-primary/20 transition"
                        data-testid={`form-link-initiative-${i.id}`}
                      >
                        <div className="font-medium truncate">{i.name}</div>
                        <div className="text-[11px] text-muted-foreground capitalize">
                          {i.type} · {i.status} · {i.area || "Sin área"}
                        </div>
                      </button>
                    ))}
                  {initiatives.length === 0 && (
                    <div className="text-xs text-muted-foreground py-3 text-center">Cargando proyectos...</div>
                  )}
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowInitSelector(true)}
                data-testid="form-pick-initiative-btn"
              >
                <Link2 className="w-3.5 h-3.5 mr-1" />Vincular a un proyecto del Portfolio (opcional)
              </Button>
            )}

            <div>
              <Label className="text-xs">Nombre del proyecto *</Label>
              <Input
                value={form.project_name}
                onChange={(e) => updateField("project_name", e.target.value)}
                placeholder="VALDESC, INNOWASTE..."
                data-testid="form-project-name"
              />
            </div>
            <div>
              <Label className="text-xs">Objetivo del proyecto *</Label>
              <Textarea
                value={form.objective}
                onChange={(e) => updateField("objective", e.target.value)}
                rows={4}
                placeholder="Desarrollar soluciones digitales y tecnologías circulares para..."
                data-testid="form-objective"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Duración (meses)</Label>
                <NumInput
                  value={form.duration_months}
                  onChange={(v) => updateField("duration_months", v)}
                  data-testid="form-duration"
                />
              </div>
              <div>
                <Label className="text-xs">Inicio (año o fecha)</Label>
                <Input
                  value={form.start_date}
                  onChange={(e) => updateField("start_date", e.target.value)}
                  placeholder="2025"
                  data-testid="form-start-date"
                />
              </div>
              <div>
                <Label className="text-xs">Fin (año o fecha)</Label>
                <Input
                  value={form.end_date}
                  onChange={(e) => updateField("end_date", e.target.value)}
                  placeholder="2027"
                  data-testid="form-end-date"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 1: Convocatoria + socios */}
        {step === 1 && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Convocatoria a la que se presenta</Label>
              <Textarea
                value={form.convocatoria}
                onChange={(e) => updateField("convocatoria", e.target.value)}
                rows={3}
                placeholder="Convocatoria de ayudas para contribuir a la mejora de..."
                data-testid="form-convocatoria"
              />
            </div>
            <div>
              <Label className="text-xs">Otros socios del consorcio</Label>
              <Textarea
                value={form.socios}
                onChange={(e) => updateField("socios", e.target.value)}
                rows={3}
                placeholder="Sika, Holcim, Knauf, Sacyr, Tecnalia..."
                data-testid="form-socios"
              />
            </div>
          </div>
        )}

        {/* STEP 2: Económico */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Presupuesto total del proyecto (€)</Label>
              <NumInput
                value={form.presupuesto_total}
                onChange={(v) => updateField("presupuesto_total", v)}
                data-testid="form-presupuesto-total"
              />
            </div>

            <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                Parte económica de Valoriza · Financiación
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[11px]">Presupuesto Valoriza</Label><NumInput value={form.financial.presupuesto} onChange={(v) => updateNested("financial", "presupuesto", v)} /></div>
                <div><Label className="text-[11px]">Ayuda total</Label><NumInput value={form.financial.ayuda} onChange={(v) => updateNested("financial", "ayuda", v)} /></div>
                <div><Label className="text-[11px]">↳ Préstamo</Label><NumInput value={form.financial.prestamo} onChange={(v) => updateNested("financial", "prestamo", v)} /></div>
                <div><Label className="text-[11px]">↳ Subvención</Label><NumInput value={form.financial.subvencion} onChange={(v) => updateNested("financial", "subvencion", v)} /></div>
                <div><Label className="text-[11px]">Deducción fiscal</Label><NumInput value={form.financial.deduccion_fiscal} onChange={(v) => updateNested("financial", "deduccion_fiscal", v)} /></div>
                <div><Label className="text-[11px]">Ahorro financiero</Label><NumInput value={form.financial.ahorro_financiero} onChange={(v) => updateNested("financial", "ahorro_financiero", v)} /></div>
              </div>
            </div>

            <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Estructura de costes
                </span>
                <span className="text-xs font-bold text-primary">Total: {fmtEUR(totalCosts)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[11px]">Mano de obra</Label><NumInput value={form.costs.mano_obra} onChange={(v) => updateNested("costs", "mano_obra", v)} /></div>
                <div><Label className="text-[11px]">Colaboraciones</Label><NumInput value={form.costs.colaboraciones} onChange={(v) => updateNested("costs", "colaboraciones", v)} /></div>
                <div><Label className="text-[11px]">Equipos</Label><NumInput value={form.costs.equipos} onChange={(v) => updateNested("costs", "equipos", v)} /></div>
                <div><Label className="text-[11px]">Material fungible</Label><NumInput value={form.costs.material_fungible} onChange={(v) => updateNested("costs", "material_fungible", v)} /></div>
                <div><Label className="text-[11px]">Auditoría</Label><NumInput value={form.costs.auditoria} onChange={(v) => updateNested("costs", "auditoria", v)} /></div>
                <div><Label className="text-[11px]">Otros</Label><NumInput value={form.costs.otros} onChange={(v) => updateNested("costs", "otros", v)} /></div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Outcomes */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-2">
                Lista de beneficios y resultados que se obtendrán si el proyecto es exitoso.
              </p>
              <div className="space-y-2">
                {(form.success_outcomes || []).map((o, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="mt-2.5 w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                      {idx + 1}
                    </span>
                    <Textarea
                      value={o}
                      onChange={(e) => updateOutcome(idx, e.target.value)}
                      rows={2}
                      placeholder="Mayor conocimiento de los vehículos eléctricos al obtener sus modelos..."
                      data-testid={`form-outcome-${idx}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive flex-shrink-0 mt-1"
                      onClick={() => removeOutcome(idx)}
                      disabled={form.success_outcomes.length <= 1}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addOutcome} data-testid="form-add-outcome" className="mt-2">
                <Plus className="w-3.5 h-3.5 mr-1" />Añadir resultado
              </Button>
            </div>

            {/* DOCUMENT REPOSITORY ATTACHER */}
            <div className="border-t pt-4">
              <Label className="text-xs flex items-center gap-1.5 mb-1">
                <Paperclip className="w-3.5 h-3.5" />
                Adjuntar documentos del repositorio I+D
              </Label>
              <p className="text-[11px] text-muted-foreground mb-2">
                Los documentos seleccionados se referenciarán en la última sección del PDF de aprobación.
              </p>
              <Input
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
                placeholder="Buscar documento por título..."
                className="h-8 mb-2"
                data-testid="form-doc-search"
              />
              <div className="border rounded-lg max-h-56 overflow-y-auto divide-y bg-muted/20">
                {!docsLoaded ? (
                  <div className="p-3 text-xs text-muted-foreground text-center">Cargando documentos...</div>
                ) : documents.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground text-center">Sin documentos en el repositorio.</div>
                ) : (
                  documents
                    .filter((d) => !docSearch || (d.title || "").toLowerCase().includes(docSearch.toLowerCase()))
                    .map((d) => {
                      const checked = (form.attached_document_ids || []).includes(d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => toggleDoc(d.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-muted/40 transition ${checked ? "bg-primary/5" : ""}`}
                          data-testid={`form-toggle-doc-${d.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {}}
                            className="w-3.5 h-3.5 flex-shrink-0 cursor-pointer accent-primary"
                          />
                          <FileText className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className={`font-medium truncate ${checked ? "text-primary" : ""}`}>
                              {d.title}
                            </div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {d.category_label} · {(d.size_bytes / 1024).toFixed(0)} KB
                            </div>
                          </div>
                        </button>
                      );
                    })
                )}
              </div>
              {(form.attached_document_ids || []).length > 0 && (
                <div className="text-[11px] text-primary mt-2 font-medium">
                  {form.attached_document_ids.length} documento(s) seleccionado(s)
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="form-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />Anterior
          </Button>
          {!isLast ? (
            <Button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} data-testid="form-next-btn">
              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={() => {
                const cleanOutcomes = (form.success_outcomes || []).map((o) => o?.trim()).filter(Boolean);
                onSave({ ...form, success_outcomes: cleanOutcomes, attached_document_ids: form.attached_document_ids || [] });
              }}
              disabled={!canSave || saving}
              data-testid="form-save-btn"
            >
              <Save className="w-4 h-4 mr-1" />{saving ? "Guardando..." : "Guardar borrador"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
